import Vue from 'vue'
import ApiClient from '@/util/ApiClient'
import Element from '../models/Element'
import { useElementStore } from './ElementStore'
import { enqueueSave } from './SaveCoordinatorStore'

/**
 * Field-level saves through the save coordinator (canvas load S.1). The optimistic change is written into the
 * stored element in place when queued, and the request body is built from the store when sent, so it carries
 * every earlier local change and never a stale copy a component held on to.
 *
 * Typed text (`content` mode) settles: a failed save resolves `undefined`/`null` and the coordinator reports it
 * (header status, snackbar) and keeps the change for a retry. Editors' autosaves do not await these promises with a
 * catch, and Pinia rethrows from a store action's promise. Discrete changes (`change` mode) reject, for callers that
 * show their own error.
 */

/** Reads a dotted path such as `message.message` or `welcome.profileText`. */
export function readPath(target: unknown, path: string): unknown {
  return path
    .split('.')
    .reduce<unknown>((value, key) => (value == null ? undefined : (value as Record<string, unknown>)[key]), target)
}

/** Writes a dotted path reactively, creating missing objects on the way. */
export function writePath(target: object, path: string, value: unknown) {
  const keys = path.split('.')
  const last = keys.pop()!
  let parent = target as Record<string, unknown>
  keys.forEach(key => {
    if (parent[key] == null) Vue.set(parent, key, {})
    parent = parent[key] as Record<string, unknown>
  })
  Vue.set(parent, last, value)
}

/** Field changes by dotted path, e.g. `{ 'proposition.imageSize': 'LARGE' }`. */
export type ElementFieldChanges = Record<string, unknown>

/**
 * How an element field save behaves (see EntrySaveMode for entries).
 * - `content`: typed text. Shown at once, merged while queued, retried, kept for a Retry; the promise settles.
 * - `change`: a discrete change (an image, a size, a contact, a link). Stored once the server accepts it, sent once;
 *   on failure the element is refetched, the user is told, and the promise rejects.
 */
export type ElementSaveMode = 'content' | 'change'

export type ElementSaveOptions = {
  /** The caller shows its own error (an inline message): no coordinator snackbar */
  handlesFailure?: boolean
}

function isPlainObject(value: unknown): value is Record<string, unknown> {
  return value !== null && typeof value === 'object' && !Array.isArray(value)
}

function sameValue(a: unknown, b: unknown): boolean {
  return JSON.stringify(a ?? null) === JSON.stringify(b ?? null)
}

/**
 * The fields `after` changes relative to `before`, by path: one level into sub-objects (`embed.height`), the whole
 * value for arrays and scalars. Undo bodies built from it touch only what the change touched.
 */
export function diffElementFields(before: Element, after: Element): ElementFieldChanges {
  const changes: ElementFieldChanges = {}
  const from = before as unknown as Record<string, unknown>
  const to = after as unknown as Record<string, unknown>
  new Set([...Object.keys(from), ...Object.keys(to)]).forEach(key => {
    if (key === 'id') return
    const was = from[key]
    const is = to[key]
    if (isPlainObject(was) && isPlainObject(is)) {
      new Set([...Object.keys(was), ...Object.keys(is)]).forEach(sub => {
        if (!sameValue(was[sub], is[sub])) changes[`${key}.${sub}`] = is[sub]
      })
    } else if (!sameValue(was, is)) {
      changes[key] = is
    }
  })
  return changes
}

/**
 * A whole-element response with the element's stored place kept: text and field saves never move an element, and
 * their response could carry a position from before a move the server answered later.
 */
export function keepPlace(response: Element): Element {
  const stored = useElementStore().elementById(response.id)
  return stored ? ({ ...response, position: stored.position, parentId: stored.parentId } as Element) : response
}

/**
 * Saves fields of an element with a whole-element PUT whose body is the stored element with `changes` on top.
 *
 * In `content` mode, saves of different fields of one element merge into one request while queued (Welcome's two
 * texts), and the later of two saves of one field replaces the earlier. In `change` mode nothing is written locally
 * until the server accepts it, and the body is built on a copy, so a rejected value never reaches the store.
 *
 * `source` is the caller's copy of the element. It is sent when the store no longer holds the element: a page reload
 * clears the page's elements before its editors unmount, and their last edit is saved on the way out.
 */
export function saveElementFields(
  source: Element,
  changes: ElementFieldChanges,
  mode: ElementSaveMode,
  options: ElementSaveOptions = {}
): Promise<Element | undefined> {
  const elementStore = useElementStore()
  const elementId = source.id
  if (elementStore.isDeletedElement(elementId)) return Promise.resolve(undefined)
  const paths = Object.keys(changes).sort()
  let lastKnown = elementStore.elementById(elementId) ?? source
  const writeChanges = (target: object) => paths.forEach(path => writePath(target, path, changes[path]))
  const verify = (serverElement: Element) =>
    paths.every(path => sameValue(readPath(serverElement, path), changes[path]))
  const put = async (body: Element) => (await ApiClient.put<Element>(`/elements/${elementId}`, body)).data
  const label = paths.join(', ')

  if (mode === 'content') {
    return enqueueSave<Element>({
      elementId,
      kind: 'content',
      target: paths.join(','),
      idempotent: true,
      wholeElement: true,
      label,
      patch: () => {
        const stored = elementStore.elementById(elementId)
        if (stored) {
          writeChanges(stored)
          lastKnown = stored
        }
        // The caller's copy as well, as before the coordinator: until the store change re-renders it, the editor
        // host compares typing against its own copy
        if (source !== stored) writeChanges(source)
      },
      send: () => put(elementStore.elementById(elementId) ?? lastKnown),
      apply: response => elementStore.mergeElementInPlace(keepPlace(response)),
      verify
    }).catch(() => undefined)
  }

  return enqueueSave<Element>({
    elementId,
    kind: 'structural',
    idempotent: false,
    label,
    reportsFailure: !options.handlesFailure,
    send: () => {
      const body = JSON.parse(JSON.stringify(elementStore.elementById(elementId) ?? lastKnown)) as Element
      writeChanges(body)
      return put(body)
    },
    apply: response => elementStore.setElement(keepPlace(response)),
    verify,
    applyVerified: serverElement => elementStore.setElement(keepPlace(serverElement))
  })
}

/** Saves one typed field (`content` mode); see saveElementFields. */
export function saveElementField(source: Element, path: string, value: unknown): Promise<Element | undefined> {
  return saveElementFields(source, { [path]: value }, 'content')
}

export type EntryList = 'faqs' | 'timelineEntries'

type Entry = { id: string }

function findEntry<E extends Entry>(element: Element | undefined, list: EntryList, entryId: string): E | undefined {
  return (element?.[list] as E[] | undefined)?.find(entry => entry.id === entryId)
}

/**
 * How an entry save behaves.
 * - `content`: typed text (an answer, a description). Shown at once, merged while queued, retried, and kept for a
 *   Retry if it fails for good.
 * - `change`: a discrete change the server validates (a question, title, due date, done flag, move, and their
 *   undo). Stored once the server accepts it, sent once, and on failure the user is told and the element is
 *   refetched. Kept in the store, a rejected value would ride along in every later save of the entry and fail it too.
 */
export type EntrySaveMode = 'content' | 'change'

/**
 * Saves fields of one FAQ or timeline entry. The body is the stored entry with `changes` on top, so saving the
 * answer never sends a question (or position) captured earlier, and undoing one field leaves the others alone.
 * `source` is the caller's copy of the element, used when the store no longer holds it (see saveElementField).
 * Resolves `null` when the entry is gone, or the save failed; with `rejects`, a failed save rejects instead (an
 * undoable change, whose undo entry must then be dropped).
 */
export function saveEntryFields<E extends Entry>(
  source: Element,
  list: EntryList,
  changes: Partial<E> & Entry,
  url: string,
  label: string,
  mode: EntrySaveMode,
  rejects = false
): Promise<E | null> {
  const elementStore = useElementStore()
  const elementId = source.id
  const entryId = changes.id
  const storedEntry = () => findEntry<E>(elementStore.elementById(elementId), list, entryId)
  let lastKnown = storedEntry() ?? findEntry<E>(source, list, entryId)
  if (!lastKnown || elementStore.isDeletedElement(elementId)) return Promise.resolve(null)
  const fields = (Object.keys(changes) as (keyof E & string)[]).filter(key => key !== 'id').sort()
  const writeFields = (entry: E | undefined) => {
    if (entry) fields.forEach(field => Vue.set(entry, field, changes[field]))
  }
  const isContent = mode === 'content'
  return enqueueSave<E>({
    elementId,
    kind: isContent ? 'content' : 'structural',
    target: isContent ? `${list}:${entryId}:${fields.join(',')}` : undefined,
    group: `${list}:${entryId}`,
    // An entry whose add failed: its saves have nowhere to go
    requires: [`${list}:${entryId}`],
    idempotent: isContent,
    label,
    patch: isContent
      ? () => {
          const stored = storedEntry()
          const own = findEntry<E>(source, list, entryId)
          writeFields(stored)
          if (own !== stored) writeFields(own)
          if (stored) lastKnown = stored
        }
      : undefined,
    // The changes on top as well: the store may hold a refetched server copy by the time a retry goes out
    send: async () => (await ApiClient.put<E>(url, { ...(storedEntry() ?? lastKnown), ...changes })).data,
    apply: response => {
      // An entry deleted while its save was in flight stays deleted
      const entries = elementStore.elementById(elementId)?.[list] as E[] | undefined
      const index = entries?.findIndex(entry => entry.id === entryId) ?? -1
      if (entries && index >= 0) entries.splice(index, 1, response)
      // A caller's copy the store no longer holds shows the accepted change too, as before
      const own = findEntry<E>(source, list, entryId)
      if (!isContent && own && own !== response) writeFields(own)
    },
    verify: serverElement => {
      const serverEntry = findEntry<E>(serverElement, list, entryId)
      return !!serverEntry && fields.every(field => (serverEntry[field] ?? null) === (changes[field] ?? null))
    },
    // A timeout can hide a write the server already stored. `apply` only runs for a response body, so without this
    // the store keeps the old entry and the next save sends it back.
    applyVerified: serverElement => {
      const serverEntry = findEntry<E>(serverElement, list, entryId)
      if (!serverEntry) return
      const entries = elementStore.elementById(elementId)?.[list] as E[] | undefined
      const index = entries?.findIndex(entry => entry.id === entryId) ?? -1
      if (entries && index >= 0) entries.splice(index, 1, serverEntry)
      const own = findEntry<E>(source, list, entryId)
      if (own && own !== serverEntry) writeFields(own)
      lastKnown = serverEntry
    }
  }).then(
    response => response ?? null,
    error => {
      if (rejects) throw error
      return null
    }
  )
}

/** Writes the stored list of FAQ or timeline entries; an element deleted meanwhile stays deleted. */
function updateStoredEntries<E extends Entry>(elementId: string, list: EntryList, update: (entries: E[]) => E[]) {
  const elementStore = useElementStore()
  const element = elementStore.elementById(elementId)
  if (!element) return
  Vue.set(element, list, update((element[list] as E[] | undefined) ?? []))
  elementStore.setElement(element)
}

/**
 * Adds a FAQ or timeline entry with its client id (the server keeps it), sent once. The entry is shown at once, so
 * what hangs off it (a timeline's connector) does not wait for the server; the server's copy replaces it, and it is
 * removed if the add fails. Saves of the entry queued after it wait for it, and are dropped if it fails. Rejects on
 * failure.
 */
export async function addEntry<E extends Entry>(
  source: Element,
  list: EntryList,
  entry: E,
  url: string,
  label: string
): Promise<E> {
  const elementId = source.id
  updateStoredEntries<E>(elementId, list, entries => [...entries.filter(e => e.id !== entry.id), entry])
  const removeShown = () => updateStoredEntries<E>(elementId, list, entries => entries.filter(e => e.id !== entry.id))
  let added: E | undefined
  try {
    added = await enqueueSave<E>({
      elementId,
      kind: 'structural',
      idempotent: false,
      provides: [`${list}:${entry.id}`],
      label,
      send: async () => (await ApiClient.post<E>(url, entry)).data,
      apply: response =>
        updateStoredEntries<E>(elementId, list, entries => [...entries.filter(e => e.id !== response.id), response])
    })
  } finally {
    if (!added) removeShown()
  }
  if (!added) throw new Error(`The ${label} was not saved: the element no longer exists or the session ended`)
  return added
}

/**
 * Deletes a FAQ or timeline entry after the saves queued for it, removing it once the server accepts. A parked save
 * of the entry is cleared with it (same group). Rejects on failure; the entry then stays.
 *
 * `entry` is the copy an undo re-adds: when the delete is applied, every earlier write of the entry has landed, so it
 * is updated from the store then (a done flag or title still in flight when delete was pressed, text typed since).
 */
export async function deleteEntry<E extends Entry>(
  source: Element,
  list: EntryList,
  entry: E,
  url: string,
  label: string
): Promise<void> {
  const elementId = source.id
  const entryId = entry.id
  await enqueueSave<void>({
    elementId,
    kind: 'structural',
    idempotent: false,
    group: `${list}:${entryId}`,
    requires: [`${list}:${entryId}`],
    succeedsOn404: true,
    label,
    send: async () => {
      await ApiClient.delete(url)
    },
    apply: () => {
      const stored = findEntry<E>(useElementStore().elementById(elementId), list, entryId)
      if (stored) Object.assign(entry, stored)
      updateStoredEntries(elementId, list, entries => entries.filter(e => e.id !== entryId))
    }
  })
}

/** The stored entry, as a copy for an undo of its delete to re-add (`deleteEntry` brings it up to date) */
export function storedEntryCopy<E extends Entry>(source: Element, list: EntryList, entry: E): E {
  const stored = findEntry<E>(useElementStore().elementById(source.id), list, entry.id)
  return { ...(stored ?? entry) }
}

/** Shows an entry's plain text field focused, with the caret at an offset (an undo or redo of an edit of it) */
export type ShowTextCallback = (text: string, caretOffset: number) => void

/**
 * Where the caret goes after an undo (in `before`) and a redo (in `after`) of a text edit: at the end of the changed
 * part, as in the text editors.
 */
export function editCaretOffsets(from?: string | null, to?: string | null): { undo: number; redo: number } {
  // The server returns `null` for a text never set, which a default value would not replace
  const before = from ?? ''
  const after = to ?? ''
  let prefix = 0
  while (prefix < before.length && prefix < after.length && before[prefix] === after[prefix]) prefix++
  let suffix = 0
  while (
    suffix < before.length - prefix &&
    suffix < after.length - prefix &&
    before[before.length - 1 - suffix] === after[after.length - 1 - suffix]
  ) {
    suffix++
  }
  return { undo: before.length - suffix, redo: after.length - suffix }
}

/** The fields that differ between two copies of an entry, from `source` (for field-level undo bodies). */
export function changedEntryFields<E extends Entry>(before: E, after: E, source: E): Partial<E> & Entry {
  const keys = new Set([...Object.keys(before), ...Object.keys(after)] as (keyof E)[])
  const changes = { id: after.id } as Partial<E> & Entry
  keys.forEach(key => {
    if (key !== 'id' && before[key] !== after[key]) changes[key] = source[key]
  })
  return changes
}
